import React, { useState } from 'react';
import { useLocalize } from 'localize-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  VehicleType,
  JourneyStatusType,
  DistanceTypeDisplayName,
  DistanceType,
  MeetingPointFileType,
} from '@transferz/fe-models';
import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import Location from '@transferz/fe-component-library/src/components/icons/LocationIcon';
import SandClock from '@transferz/fe-component-library/src/components/icons/SandClock';
import Time from '@transferz/fe-component-library/src/components/icons/ClockIcon';
import Timer from '@transferz/fe-component-library/src/components/icons/TimerIcon';
import { formatTime, futureDate } from '../../../../utils/utils';
import { ExtendedCard } from '../../../../components/card/ExtendedCard';
import { GeoPoint } from '../../../../components/geoPoint/GeoPoint';
import { ReactComponent as ZoomInIcon } from '../../../../assets/svgs/zoomInIcon.svg';
import { ReactComponent as ZoomOutIcon } from '../../../../assets/svgs/zoomOutIcon.svg';
import { Modal } from '../../../../components/modal/Modal';
import './meetingPoint.scss';

export const MeetingPoint: React.FunctionComponent<any> = ({ journey }) => {
  const { translate } = useLocalize();
  const flags = useFlags();
  const [zoomOutEnabled, setZoomOutEnabled] = useState<any>(false);
  const journeyIsPending = journey?.status === JourneyStatusType.Pending;
  const isCollapsedStatus = journey?.status === JourneyStatusType.CancelledWithCosts || journey?.status === JourneyStatusType.CancelledFree;
  const isMapShown = !isCollapsedStatus && !journeyIsPending;
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedStatus);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const title = journeyIsPending || isCollapsedStatus || journey?.status === JourneyStatusType.Confirmed || journey?.status === JourneyStatusType.Planned
    ? translate('meetingPoint')
    : (journey?.status === JourneyStatusType.DriverUnderway || journey?.status === JourneyStatusType.DriverArrived)
      ? translate('meetingPointMeetDriverTitle')
      : journey?.status === JourneyStatusType.JourneyInProgress
        ? translate('meetingPointInProgressTitle')
        : translate('meetingPointCompletedTitle');

  const getPickupTimeTitle = (pickupDate: string, pickUpTimeZone: string) => {
    const diffInHours = futureDate(pickupDate, pickUpTimeZone, 'hours');
    const diffInMinutes = futureDate(pickupDate, pickUpTimeZone, 'minutes');
    if (diffInHours < 1) {
      return `${translate('in')} ${diffInMinutes} ${translate('minutes')}`;
    } else if (diffInHours < 48) {
      return `${translate('in')} ${diffInHours} ${translate('hour')}`;
    } else {
      return `${translate('in')} ${Math.floor(diffInHours / 24)} ${translate('days')}`;
    }
  };
  const pickupTimeTitle = getPickupTimeTitle(journey?.journeyExecutionInfo?.pickupDate, journey?.journeyExecutionInfo?.pickupTimeZone);
  const meetingPointFiles = journey?.meetingPoint?.meetingPointFiles;
  const isSharedVehicleCategory = (vehicleCategory: any) => {
    return (vehicleCategory === VehicleType.Shuttle
      || vehicleCategory === VehicleType.SpeedyShuttle
      || vehicleCategory === VehicleType.DirectShuttle
      || vehicleCategory === VehicleType.EventShuttle
    );
  };

  const isCompleted = journey?.status === JourneyStatusType.Completed;

  const handleZoomIn = () => {
    setZoomOutEnabled(true);
    const imageContainer = document.getElementById('image-container');
    const image = document.getElementById('meeting-point-image');
    const imageContainerWidth = imageContainer?.clientWidth;
    const imageContainerHeight = imageContainer?.clientHeight;
    (imageContainer as any).style.width = imageContainerWidth + 'px';
    (imageContainer as any).style.height = imageContainerHeight + 'px';
    (imageContainer as any).style.overflow = 'scroll';
    (image as any).style.width = (image?.clientWidth as any + 100) + 'px';
    (image as any).style.height = 'auto';
  };
  const handleZoomOut = () => {
    const imageContainer = document.getElementById('image-container');
    const image = document.getElementById('meeting-point-image');
    const imageContainerWidth = imageContainer?.clientWidth;
    (imageContainer as any).style.overflow = 'scroll';
    (image as any).style.width = (image?.clientWidth as any - 100) + 'px';
    (image as any).style.height = 'auto';
    if ((image as any)?.clientWidth <= (imageContainerWidth as any)) {
      handleZoomReset();
    }
  };

  const handleZoomReset = () => {
    const imageContainer = document.getElementById('image-container');
    const image = document.getElementById('meeting-point-image');
    (imageContainer as any).removeAttribute('style');
    (image as any).removeAttribute('style');
    setZoomOutEnabled(false);
  };

  return (
    <div id="moreMeetingInfo" className={`meeting-point-details ${flags?.showNewDesign ? 'card-wrap' : ''} ${isCompleted ? 'completed' : ''}`}>
      {flags?.showNewDesign
        ? (
          <Card
            title={title}
            collapsed={isCollapsed}
            onCollapse={() => setIsCollapsed(!isCollapsed)}
            isGrey={isCollapsedStatus || journeyIsPending}
            collapsibleIcon={!(isCollapsedStatus || journeyIsPending)}
          >
            <>
              {journey?.status === JourneyStatusType.Pending && (
                <div className="d-flex">
                  <SandClock color="asphalt7" className="bottom" />
                  <Typography variant="body1">{translate('meetingPointPending')}</Typography>
                </div>
              )}
              {(journey?.status === JourneyStatusType.CancelledFree || journey?.status === JourneyStatusType.CancelledWithCosts) && (
                <div className="d-flex">
                  <SandClock color="asphalt7" className="bottom" />
                  <Typography variant="body1">{translate('meetingPointCancelled')}</Typography>
                </div>
              )}
              {isMapShown && (
                <>
                  {(journey?.status === JourneyStatusType.Confirmed || journey?.status === JourneyStatusType.Planned)
                    && (<div data-testid="info_pickup-time" className="d-flex mb-24 pickup-time">
                      <Time color="asphalt7" direction="right" />
                      <Typography variant="body1">{formatTime(journey?.journeyExecutionInfo?.pickupDate, 'HH:mm')}</Typography>&nbsp;
                      <Typography variant="subtitle1" color="atlantis5">{pickupTimeTitle}</Typography>
                    </div>
                    )}

                  {journey?.inbound && !(journey?.status === JourneyStatusType.JourneyInProgress || journey?.status === JourneyStatusType.Completed)
                    ? (
                      <div className="d-flex mb-24">
                        <i><Location color="asphalt7" /></i>
                        <Typography variant="body1">{translate('meetingPointInbound')}</Typography>
                      </div>
                    )
                    : (journey?.inbound)
                      ? (
                        <div className="d-flex mb-24">
                          <i><Location color="asphalt7" /></i>
                          <Typography variant="body1">{journey?.destinationLocation?.address?.originalAddress}</Typography>
                        </div>
                      )
                      : null
                  }

                  {!journey?.inbound && (journey?.status === JourneyStatusType.JourneyInProgress || journey?.status === JourneyStatusType.Completed) && (
                    <div className="d-flex mb-24">
                      <i><Location color="asphalt7" /></i>
                      <Typography variant="body1" color="asphalt5">{journey?.destinationLocation?.address?.formattedAddress}</Typography>
                    </div>
                  )}
                  {journey?.journeyExecutionInfo?.durationInSeconds && journey?.journeyExecutionInfo?.distance?.value && journey?.status === JourneyStatusType.JourneyInProgress && (<div className="d-flex mb-24">
                    <Timer color="asphalt7" />
                    <Typography variant="body1">
                      {journey?.journeyExecutionInfo?.durationInSeconds} min&nbsp;
                      {(journey?.journeyExecutionInfo?.distance?.value).toFixed(1)} ({DistanceTypeDisplayName[journey?.journeyExecutionInfo?.distance?.unit as DistanceType]})
                    </Typography>
                  </div>
                  )}
                  {journey?.meetingPoint?.description && (journey?.status === JourneyStatusType.Confirmed || journey?.status === JourneyStatusType.Planned || journey?.status === JourneyStatusType.DriverArrived || journey?.status === JourneyStatusType.DriverUnderway) && (
                    <div className="d-flex mb-24">
                      <i><Location color="asphalt7" /></i>
                      <Typography variant="body1">{journey?.meetingPoint?.description}</Typography>
                    </div>
                  )}
                  {journey?.meetingPoint && (journey?.status === JourneyStatusType.Confirmed
                    || journey?.status === JourneyStatusType.Planned
                    || journey?.status === JourneyStatusType.DriverUnderway
                    || journey?.status === JourneyStatusType.DriverArrived
                  ) && !isSharedVehicleCategory(journey?.journeyExecutionInfo?.vehicleCategory) && (
                    <GeoPoint
                      position={journey?.meetingPoint?.geographicLocation}
                      center={journey?.meetingPoint?.geographicLocation}
                      name="geographicLocation"
                      validationMsg={''}
                      showUserPosition={true}
                      userPositionIcon={<div>
                        <svg width="48" height="48" viewBox="0 0 40 40">
                          <circle cx="20" cy="20" fill="none" r="8" stroke="#0083A8" strokeWidth="2">
                            <animate attributeName="r" from="8" to="20" dur="1.5s" begin="0s" repeatCount="indefinite"/>
                            <animate attributeName="opacity" from="1" to="0" dur="1.5s" begin="0s" repeatCount="indefinite"/>
                          </circle>
                          <circle cx="20" cy="20" fill="#0083A8" r="10"/>
                        </svg>
                      </div>}
                      zoom={17}
                      draggable={false}
                    />
                  )}
                  {journey?.status === JourneyStatusType.JourneyInProgress && (
                    <GeoPoint
                      position={journey?.destinationLocation?.position}
                      center={journey?.destinationLocation?.position}
                      name="geographicLocation"
                      validationMsg={''}
                      showUserPosition={true}
                      userPositionIcon={<div>
                        <svg width="48" height="48" viewBox="0 0 40 40">
                          <circle cx="20" cy="20" fill="none" r="8" stroke="#0083A8" strokeWidth="2">
                            <animate attributeName="r" from="8" to="20" dur="1.5s" begin="0s" repeatCount="indefinite"/>
                            <animate attributeName="opacity" from="1" to="0" dur="1.5s" begin="0s" repeatCount="indefinite"/>
                          </circle>
                          <circle cx="20" cy="20" fill="#0083A8" r="10"/>
                        </svg>
                      </div>}
                      zoom={17}
                      draggable={false}
                    />
                  )}
                  {!!meetingPointFiles?.length && (
                    <>
                      <div className="meeting-point-images" >
                        {meetingPointFiles.map((item: any, index: number) => (
                          <div
                            className="meeting-point-item"
                            key={index}
                            onClick={() => {
                              setIsPopupOpen(true);
                              setSelectedImageUrl(`${window.tz_globalConfigs.storageFilesDownloadUrl}/download?externalKey=${item.file.externalKey}`);
                              setSelectedImageTitle(item.fileType === MeetingPointFileType.Map
                                ? translate('mapWithInstructions')
                                : item.fileType === MeetingPointFileType.Desk
                                  ? translate('photoOfTheOffice')
                                  : item.fileType === MeetingPointFileType.Pickup
                                    ? translate('photoOfThePickupPoint')
                                    : translate('openForMoreInformation'));
                            }
                            }
                          >
                            <div
                              className="meeting-point-image"
                              style={{ backgroundImage: `url(${window.tz_globalConfigs.storageFilesDownloadUrl}/download?externalKey=${item.file.externalKey})` }}>
                            </div>
                            <div className="zoom-in-button">
                              <ZoomInIcon fill="#0083A8" />
                              {item.fileType === MeetingPointFileType.Map && <div>{translate('mapWithInstructions')}</div>}
                              {item.fileType === MeetingPointFileType.Desk && <div>{translate('photoOfTheOffice')}</div>}
                              {item.fileType === MeetingPointFileType.Pickup && <div>{translate('photoOfThePickupPoint')}</div>}
                              {item.fileType === MeetingPointFileType.Other && <div>{translate('openForMoreInformation')}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                      {isPopupOpen && (
                        <Modal
                          title={selectedImageTitle}
                          show={isPopupOpen}
                          bodyText={(<div className="image-zoom-container-wrapper">
                            <div id="image-container" className="image-container">
                              <img id="meeting-point-image" onClick={() => setIsPopupOpen(true)} src={selectedImageUrl} />
                            </div>
                            <div className="zoom-icons-wrapper">
                              <div className="zoom-icon-wrapper zoom-out-icon">
                                <ZoomOutIcon className={zoomOutEnabled ? '' : 'zoom-icon-disabled'} fill="#0083A8" onClick={zoomOutEnabled ? () => handleZoomOut() : (e) => e.preventDefault() }/>
                              </div>
                              <div className="zoom-icon-wrapper zoom-in-icon"  onClick={() => handleZoomIn()}>
                                <ZoomInIcon fill="#0083A8"/>
                              </div>
                            </div>
                          </div>) as any}
                          showConfirm={false}
                          onClose={() => {
                            setIsPopupOpen(false);
                            setSelectedImageUrl('');
                            handleZoomReset();
                          }}
                          onConfirm={() => {}}
                          containerClassName="meeting-point-image-modal"
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </>
          </Card>
        )
        : (
          <ExtendedCard title={translate('meetingPoint')} isEditable={true}>
            {journey?.meetingPoint
              ? (
                <div>
                  <div className="text-bold">{translate('meetingPoint')}</div>
                  <div className="text-normal meeting-text">
                    {journey?.meetingPoint?.description}
                  </div>
                  <GeoPoint
                    position={journey?.meetingPoint?.geographicLocation}
                    center={journey?.meetingPoint?.geographicLocation}
                    name="geographicLocation"
                    validationMsg={''}
                    zoom={17}
                    draggable={false}
                  />
                </div>
              )
              : <div className="text-normal">{translate('noMeetingPoint')}</div>
            }
          </ExtendedCard>
        )

      }
    </div>
  );
};
